// src/services/user/UserRentalsService.js
import { useState, useCallback } from "react";
import API from "../../api/apiUser";
import { useNotification } from "../../context/NotificationContext";

export function useUserRentals() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const { showNotification } = useNotification();

  const fetchRentals = useCallback(async (page = 1, status = "All") => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 10 };
      if (status !== "All") params.status = status;

      const res = await API.get("/user/rentals", { params });

      if (res.data.status === "success") {
        setRentals(res.data.data);
        setPagination(res.data.pagination);
      } else {
        throw new Error(res.data.message || "Failed to fetch rentals");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error fetching rentals";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Error",
        message: msg,
      });
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const cancelRental = async (invoiceId) => {
    try {
      const res = await API.post(`/user/rentals/${invoiceId}/cancel`);
      if (res.data.status === "success") {
        return { success: true };
      } else {
        throw new Error(res.data.message || "Failed to cancel invoice");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error cancelling invoice";
      return { success: false, error: msg };
    }
  };

  const getRentalDetail = async (invoiceId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get(`/user/rentals/${invoiceId}`);
      if (res.data.status === "success") {
        return { success: true, data: res.data.data };
      } else {
        throw new Error(res.data.message || "Failed to fetch rental details");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error fetching rental details";
      setError(msg);
      showNotification({
        type: "failed",
        title: "Load Error",
        message: msg,
      });
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const downloadRental = async (invoiceId) => {
    return API.get(`/user/rentals/${invoiceId}/download`, {
      responseType: 'blob'
    });
  };

  const getDashboardStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get("/user/rentals/dashboard");
      if (res.data.status === "success") {
        return { success: true, data: res.data.data };
      } else {
        throw new Error(res.data.message || "Failed to fetch dashboard stats");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error fetching dashboard stats";
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const getUserActivities = async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get("/user/rentals/activity", { params: { page, limit } });
      if (res.data.status === "success") {
        return { success: true, data: res.data.data, pagination: res.data.pagination };
      } else {
        throw new Error(res.data.message || "Failed to fetch activities");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error fetching activities";
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  return {
    rentals,
    loading,
    error,
    pagination,
    fetchRentals,
    getRentalDetail,
    cancelRental,
    downloadRental,
    getDashboardStats,
    getUserActivities,
  };
}
